export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  keyword: string;
  category: string;
  readTime: string;
  publishedAt: string; // ISO date
  updatedAt: string;
  author: string;
  body: string; // markdown
}

export const posts: BlogPost[] = [
  {
    slug: "what-is-vat-south-africa",
    title: "What is VAT in South Africa and How Does It Work?",
    description:
      "A plain-English guide to VAT in South Africa: how VAT works, when your business must register, what the 15% rate means, and how to stay compliant with SARS.",
    keyword: "vat south africa",
    category: "SARS",
    readTime: "7 min read",
    publishedAt: "2026-04-02",
    updatedAt: "2026-04-02",
    author: "Bookkeeping Services South Africa",
    body: `## VAT in South Africa, explained simply

VAT, or Value-Added Tax, is a tax added to most goods and services sold in South Africa. The standard VAT rate is currently 15%.

If your business is registered for VAT, you don't keep that VAT as extra profit. You collect it from customers on behalf of SARS, declare it on your VAT return, and pay over what is due.

For many small businesses, VAT becomes confusing because it affects invoicing, pricing, cash flow and SARS compliance. The good news is that once you understand the basics, it becomes much easier to manage.

## When must a business register for VAT?

There are two VAT registration thresholds South African businesses should know.

**Compulsory VAT registration:**
Your business must register for VAT if your taxable turnover is more than **R2.3 million** in any continuous 12-month period.

**Voluntary VAT registration:**
You may apply to register voluntarily if your taxable turnover is more than **R120,000** in a 12-month period, provided you meet SARS' requirements.

Taxable turnover generally means income from goods or services that are subject to VAT, either at the standard rate of 15% or at the zero rate. Exempt income is treated differently.

Once your business is registered as a VAT vendor, SARS will issue you with a VAT number. From that point, you must charge VAT on taxable supplies, issue proper tax invoices, submit VAT201 returns, and keep proper records.

## How VAT actually works

VAT works on an output VAT minus input VAT basis.

- **Output VAT** is the VAT you charge your customers on sales.
- **Input VAT** is the VAT you pay on business expenses that you are allowed to claim back.

At the end of your VAT period, you calculate the difference.

For example, if your business charged customers R30,000 in VAT during the period and paid R10,000 VAT on claimable business expenses, you would pay SARS the difference:

R30,000 output VAT – R10,000 input VAT = **R20,000 payable to SARS**

If your input VAT is more than your output VAT, you may have a VAT refund due, depending on your VAT return and supporting documents.

## What does the 15% VAT rate mean for your business?

The 15% VAT rate means that a VAT-registered business must add VAT to most taxable goods and services.

For example:

- If your service price is R1,000 excluding VAT, the VAT is R150. Your customer pays R1,150 in total.
- If your price is VAT-inclusive at R1,150, the VAT portion is R150 and the amount before VAT is R1,000.

This is why VAT must be handled carefully in your pricing. A common mistake is quoting a client without thinking about whether the amount is VAT-inclusive or VAT-exclusive. That can quickly affect your profit margin.

In South Africa, VAT vendors must also make sure that prices advertised or quoted to customers are VAT-inclusive, unless it is made very clear that VAT still needs to be added.

## VAT periods and VAT201 returns

Most small businesses submit VAT returns every two months. SARS will place your business into a VAT category, usually Category A or Category B.

- **Category A** periods end in January, March, May, July, September and November.
- **Category B** periods end in February, April, June, August, October and December.

Your VAT return is submitted on a VAT201 through SARS eFiling. If you use eFiling, the VAT return and payment are usually due by the last business day of the month after your VAT period ends.

For example, if your VAT period ends on 30 April, your VAT201 and payment are due by the last business day of May.

Late VAT payments can result in a 10% penalty, plus interest. This is one of the easiest ways for small businesses to lose money unnecessarily, especially when VAT has not been planned for properly.

## Standard-rated, zero-rated and exempt supplies

Not everything is treated the same for VAT.

- **Standard-rated supplies** are taxed at 15%. Most goods and services fall into this category.
- **Zero-rated supplies** are taxed at 0%. Examples include certain basic foodstuffs and exports, provided the correct requirements are met.
- **Exempt supplies** are not subject to VAT. Examples can include certain financial services, residential rental and public passenger transport.

Zero-rated and exempt are not the same thing.

If you make zero-rated supplies, you may still be able to claim input VAT on related expenses. If you make exempt supplies, you generally cannot claim input VAT on expenses linked to those exempt supplies.

This is where many businesses get caught, especially if they have a mix of taxable and exempt income.

## Common VAT mistakes businesses make

Here are some of the most common VAT issues we see with South African businesses:

- Charging VAT before the business is actually VAT-registered.
- Forgetting to register when turnover passes the compulsory registration threshold.
- Treating VAT collected from customers as business cash flow.
- Submitting VAT201 returns late or paying SARS after the deadline.
- Claiming VAT on personal expenses paid through the business.
- Claiming VAT without a valid tax invoice.
- Issuing invoices without the required VAT details.
- Claiming input VAT on entertainment expenses where it is not allowed.
- Not separating standard-rated, zero-rated and exempt income correctly.

These mistakes can lead to penalties, interest, SARS queries and unnecessary stress.

## What records should you keep?

If your business is VAT-registered, you need to keep proper records for your VAT returns. This includes:

- Sales invoices issued to customers.
- Supplier invoices for expenses.
- Bank statements.
- Proof of payment.
- Credit notes and debit notes.
- Import and export documents, where relevant.
- Supporting documents for any VAT claimed.

Good bookkeeping makes VAT much easier. Poor records make VAT stressful, especially if SARS requests supporting documents or reviews a refund.

## Do you need help with VAT?

VAT can be simple when it is set up properly, but expensive when it is ignored.

We assist South African businesses with VAT registration, VAT201 returns, bookkeeping and SARS compliance. Whether you need to register for VAT, catch up on returns, or check that your VAT is being handled correctly, we can help.

Book a free consultation and we'll review your VAT setup, explain what needs to be done, and help you stay compliant with SARS.
`,

  },
  {
    slug: "what-is-a-balance-sheet",
    title: "What is a Balance Sheet? A Simple Guide for SA Business Owners",
    description:
      "Your balance sheet shows the real financial position of your business — not just how much cash is sitting in the bank. Here's how to understand it in plain English.",
    keyword: "balance sheet",
    category: "Bookkeeping",
    readTime: "6 min read",
    publishedAt: "2026-04-05",
    updatedAt: "2026-04-05",
    author: "Bookkeeping Services South Africa",
    body: `## Why your bank balance does not tell the full story

Most business owners check their bank balance every day.

That makes sense. Cash is important.

But your bank balance only tells you how much money is available right now. It does not tell you who still owes you money, what bills are still unpaid, how much debt the business has, or whether the business is actually becoming stronger over time.

That is where the balance sheet comes in.

A balance sheet, also called a statement of financial position, shows what your business owns, what it owes, and what is left for the owner at a specific date.

Think of it as a financial snapshot of your business.

## The balance sheet equation

Every balance sheet follows one basic rule:

\`\`\`text
Assets = Liabilities + Equity
\`\`\`

In simple terms:

- **Assets** are what the business owns.
- **Liabilities** are what the business owes.
- **Equity** is the owner's interest in the business after liabilities are taken into account.

If the balance sheet does not balance, something is wrong in the bookkeeping.

## What are assets?

Assets are the things your business owns or controls.

These are usually split into current assets and non-current assets.

### Current assets

Current assets are assets that are expected to turn into cash, or be used up, within the next 12 months.

Examples include:

- Cash in the bank.
- Money owed by customers.
- Stock on hand.
- Short-term deposits.
- VAT refunds due from SARS.

In South Africa, many small businesses struggle because too much money sits in debtors. On paper, the business may have made sales, but if customers have not paid, that money is not available to pay salaries, suppliers or SARS.

### Non-current assets

Non-current assets are longer-term assets used in the business.

Examples include:

- Vehicles.
- Computers and laptops.
- Machinery.
- Office equipment.
- Furniture.
- Property.

These assets help the business operate, but they are not the same as cash. A delivery vehicle may be valuable, but it cannot pay your VAT bill unless you sell it.

## What are liabilities?

Liabilities are amounts the business owes to other people, suppliers, banks or SARS.

These are also usually split into current liabilities and non-current liabilities.

### Current liabilities

Current liabilities are amounts that must usually be paid within the next 12 months.

Examples include:

- Supplier accounts.
- Business credit cards.
- Short-term loans.
- VAT payable to SARS.
- PAYE and UIF payable.
- Accrued expenses.
- Unpaid salaries or wages.

This section is important because it shows what pressure the business is under in the short term.

A business may have money in the bank, but if VAT, PAYE, supplier bills and loan payments are all due soon, the cash position may not be as strong as it looks.

### Non-current liabilities

Non-current liabilities are longer-term debts.

Examples include:

- Vehicle finance.
- Long-term business loans.
- Mortgage bonds.
- Asset finance.

These amounts are not all due immediately, but they still affect the overall financial strength of the business.

## What is equity?

Equity is the owner's interest in the business according to the accounting records.

It is usually made up of:

- Money the owner put into the business.
- Profit retained in the business over time.
- Less drawings or distributions taken out by the owner.

It is important to understand that equity is not always the same as the selling price of the business.

A business may have strong equity on paper, but its market value depends on many other things, such as profits, customers, contracts, goodwill and future earning potential.

The balance sheet shows the accounting position, not necessarily what someone would pay for the business.

## What does a balance sheet tell you?

A balance sheet helps you answer important questions about your business.

- Can the business pay its short-term bills?
- Are customers paying on time?
- Is too much money tied up in stock?
- How much does the business owe SARS?
- How much debt is the business carrying?
- Is the owner taking out more than the business can afford?
- Is the business becoming stronger or weaker over time?

This is why a balance sheet is so important. It shows the things that your bank balance cannot show.

## A simple example

Let's say your business has the following:

- Cash in the bank: **R80,000**
- Customers owing you money: **R120,000**
- Equipment: **R50,000**

**Total assets: R250,000**

Now let's say the business owes:

- Suppliers: **R70,000**
- VAT payable to SARS: **R25,000**
- Vehicle finance: **R60,000**

**Total liabilities: R155,000**

That leaves **equity of R95,000**.

So the balance sheet would show:

- Assets: R250,000
- Liabilities: R155,000
- Equity: R95,000

The business may have R80,000 in the bank, but it also has R155,000 in liabilities. That is why looking at the bank balance alone can be misleading.

## Why profitable businesses can still run out of cash

A business can make a profit and still have cash flow problems.

This usually happens when:

- Customers take too long to pay.
- Stock is not moving.
- VAT money is used for normal expenses.
- Loan repayments are too high.
- Owners draw too much money from the business.
- Supplier bills are not tracked properly.

The income statement may show that the business made a profit, but the balance sheet shows where the money actually went.

That is why business owners should review both reports together.

## How often should you review your balance sheet?

A business owner should review the balance sheet at least once a month.

You do not need to understand every accounting term, but you should know the basics:

- How much cash is available?
- How much is owed by customers?
- How much is owed to suppliers?
- How much is owed to SARS?
- How much debt does the business have?
- Is equity improving over time?

If you are not getting a balance sheet every month, your bookkeeping is not giving you the full picture.

## We prepare monthly management reports

At Bookkeeping Services South Africa, we prepare monthly financial reports for our clients, including a balance sheet and income statement.

This gives you a clear view of your business every month, not just at year-end.

You will know what your business owns, what it owes, what SARS amounts are due, and whether your business is moving in the right direction.

Our reports are prepared by experienced South African accounting professionals, so you can make decisions with proper numbers in front of you.

No guessing. No messy spreadsheets. Just proper monthly bookkeeping and financial reporting for South African businesses.
`,
  },
  {
    slug: "tax-clearance-certificate-south-africa",
    title: "What is a Tax Clearance Certificate (TCS) and When Do You Need One?",
    description:
      "Tax Clearance has changed in South Africa. Here's what a Tax Compliance Status PIN is, how it works, and when your business may be asked for one.",
    keyword: "tax clearance certificate",
    category: "SARS",
    readTime: "5 min read",
    publishedAt: "2026-04-08",
    updatedAt: "2026-04-08",
    author: "Bookkeeping Services South Africa",
    body: `## The old Tax Clearance Certificate has changed

Many South Africans still say "Tax Clearance Certificate", but SARS no longer works mainly with the old printed certificate system.

Today, SARS uses the Tax Compliance Status, also known as TCS.

Instead of handing someone a once-off paper certificate, SARS gives you a TCS PIN. This PIN allows a third party, such as a tender department, bank, funder or supplier onboarding team, to check your tax compliance status online.

The important thing to understand is that your tax compliance status is not fixed forever.

It can change.

You may be compliant today, but if a return becomes overdue or a SARS debt is not dealt with, your status can change to non-compliant. That is why keeping your SARS profile up to date matters.

## Is it the same as a Tax Clearance Certificate?

In everyday business language, yes.

When someone asks for your "Tax Clearance Certificate", they usually mean your TCS PIN or proof that your business is tax compliant with SARS.

So if a tender document, landlord, bank or corporate client asks for tax clearance, they are normally asking for your SARS Tax Compliance Status.

## When do you need a TCS PIN?

Your business may be asked for a TCS PIN when:

- You are applying for a government tender.
- You are being added as a supplier to a corporate client.
- You are applying for certain business loans or funding.
- You are applying for grants or supplier development programmes.
- You are renewing certain licences or registrations.
- You need to prove that your tax affairs are in order.

For most normal business situations, the TCS type used is **Good Standing**.

This is the option that confirms your tax affairs are in order with SARS.

## What about foreign investment allowance and emigration?

SARS has changed this wording as well.

Foreign Investment Allowance and Emigration applications are now dealt with under **Approval International Transfer**, also called AIT.

AIT is generally used where funds are being transferred internationally and SARS approval is required.

For most South African businesses that simply need tax clearance for tenders, funding or supplier onboarding, Good Standing is the relevant option.

## What does "tax compliant" actually mean?

Being tax compliant means your SARS affairs are in order.

In plain English, SARS wants to see that:

- Your business is registered for the correct taxes.
- Your tax returns have been submitted.
- You do not have outstanding SARS debt, unless an approved payment arrangement or suspension is in place.
- Your registered details are up to date.

This can include Income Tax, VAT, PAYE, UIF and other tax types, depending on your business.

For example, if your company is registered for VAT and PAYE, SARS will look at whether your VAT201 returns, EMP201 returns and income tax returns are up to date.

One missing return can cause a red compliance status.

## How to get a TCS PIN from SARS

You can request a TCS PIN through SARS eFiling.

The basic process is:

1. Log in to SARS eFiling.
2. Go to the **Tax Status** section.
3. Open your Tax Compliance Status profile.
4. Check your **My Compliance Profile**.
5. Select **Tax Compliance Status Request**.
6. Choose the correct type, usually **Good Standing**.
7. Submit the request.

If your tax affairs are in order, SARS will issue a PIN.

You can then give the PIN to the person or organisation requesting proof of compliance.

They will use the PIN to verify your status online.

## Why your TCS can show red

A red status usually means SARS has picked up something that must be fixed.

Common reasons include:

- Outstanding income tax returns.
- Outstanding VAT201 returns.
- Outstanding PAYE returns.
- EMP501 reconciliations not submitted.
- Administrative penalties.
- SARS debt not paid.
- Banking or registered details not updated.
- Tax types not correctly linked on eFiling.

This is where many business owners get stuck. The issue is not always from the current year. Sometimes it comes from old returns or old SARS registrations that were never properly cleaned up.

## Can you still get a TCS PIN if you owe SARS money?

Sometimes, yes.

If you owe SARS money, your status may be non-compliant. However, if SARS has approved a payment arrangement or a suspension of payment, your compliance status may be restored, depending on your overall SARS profile.

This is why it is important not to ignore SARS debt.

If you cannot pay the full amount immediately, the better approach is to deal with it properly and apply for an arrangement where possible.

## Why TCS matters for tenders

For tenders, your TCS status can be the difference between being considered and being rejected.

A tender board or procurement department does not want to guess whether your business is tax compliant. They want to verify it directly with SARS.

Even if your pricing is competitive and your documents are strong, a red SARS status can create problems.

Before applying for tenders, it is a good idea to check your TCS status first. That gives you time to fix any issues before the deadline.

## We help fix SARS non-compliance

At Bookkeeping Services South Africa, we assist businesses with SARS compliance, outstanding returns, VAT, PAYE, income tax and TCS issues.

If your TCS status is red, we can help identify what SARS is flagging and what needs to be corrected.

We also assist with missing returns, SARS debt arrangements and general compliance clean-up, so your business can get back into good standing.

If you need a TCS PIN for a tender, funding application or supplier registration, get in touch for a free compliance review.

We will check what is outstanding, explain the next steps, and help you get your SARS profile in order.
`,
  },

  {
    slug: "provisional-tax-south-africa",
    title: "Provisional Tax in South Africa: Who Pays It and How It Works",
    description:
      "Provisional tax catches many small business owners by surprise. Here's who must pay it, when IRP6 returns are due, and how to avoid unnecessary SARS penalties.",
    keyword: "provisional tax",
    category: "SARS",
    readTime: "6 min read",
    publishedAt: "2026-04-11",
    updatedAt: "2026-06-27",
    author: "Bookkeeping Services South Africa",
    body: `## Provisional tax in simple terms

Provisional tax is not a separate tax.

It is a way of paying your normal income tax during the tax year, instead of waiting for one big tax bill after your annual return is assessed.

SARS uses provisional tax because many people and businesses earn income where PAYE is not deducted every month.

For example, if you run a business, invoice clients, earn rental income or receive income outside a normal salary, SARS still expects tax to be paid during the year.

That is what provisional tax is for.

## Who must pay provisional tax?

You may be a provisional taxpayer if you earn income that is not taxed through PAYE.

This can include:

- Companies.
- Close corporations.
- Sole proprietors.
- Freelancers.
- Consultants.
- Landlords earning rental income.
- People with investment income above the allowed limits.
- People earning income from more than one source.

If your only income is a salary and your employer deducts PAYE correctly every month, you are generally not a provisional taxpayer.

For companies, provisional tax is normally part of doing business. For individuals, it depends on the type of income you earn and whether you fall within SARS' exclusions.

## Common examples

- A small business owner trading through a Pty Ltd company will usually submit provisional tax returns.
- A freelancer invoicing clients every month will usually be a provisional taxpayer.
- A landlord earning taxable rental income may need to submit provisional tax returns.
- A person who only earns a salary from one employer, with PAYE deducted every month, usually does not.

This is why many people only discover provisional tax after they start a business, buy a rental property or begin earning income on the side.

## What is an IRP6?

The IRP6 is the provisional tax return submitted to SARS.

It is not the same as your annual income tax return.

On the IRP6, you estimate your taxable income for the full year and calculate the provisional tax due.

Even if the amount payable is nil, a provisional taxpayer may still need to submit the IRP6 return.

This is where many business owners make mistakes. They assume that because there is no payment due, there is no return to submit. That can still create SARS issues.

## When are provisional tax returns due?

For individuals, the tax year runs from 1 March to the last day of February.

The usual provisional tax deadlines are:

- **First period:** due within **6 months from the start of the year of assessment** (for individuals with a February year-end, this falls at the end of August).
- **Second period:** due on the **last business day of the year of assessment** (for individuals, the last business day of February).
- **Third period:** a **voluntary top-up** payment, used to reduce interest where the first two estimates were too low. For individuals and February year-end companies, this is usually due at the end of September.

For companies, the same idea applies, but the dates follow the company's financial year.

The first provisional tax return is due 6 months into the company's year of assessment.

The second provisional tax return is due on the last business day of the company's year of assessment.

The third payment is voluntary and generally falls 6 months after year-end (or end of September for February year-ends).

## How is provisional tax calculated?

Each provisional tax return is based on an estimate of your taxable income for the full year.

Taxable income is not the same as turnover.

Turnover is your total sales or income before expenses.

Taxable income is the amount left after deducting allowable business expenses and making the correct tax adjustments.

The basic process is:

1. Estimate your taxable income for the full year.
2. Calculate the tax on that estimated taxable income.
3. Deduct PAYE already paid, if applicable.
4. Deduct previous provisional tax payments already made.
5. Pay the balance due for that provisional period.

### First provisional payment

The first IRP6 is submitted halfway through the tax year.

At this point, you estimate your taxable income for the full year and pay roughly half of the expected annual tax, after taking into account PAYE or other tax credits where applicable.

For individuals with a February year-end, this is normally due at the end of August.

For companies, it is due six months after the start of the company's financial year.

### Second provisional payment

The second IRP6 is submitted at the end of the tax year.

This is the more important one because your estimate should be much closer to your actual taxable income.

You calculate the estimated tax for the full year, deduct what was already paid on the first IRP6, deduct PAYE if applicable, and pay the remaining amount.

For individuals, this is normally due on the last business day of February.

For companies, it is due at the end of the company's financial year.

### Third provisional payment

The third payment is voluntary.

It is often called a top-up payment.

It is used where the first and second provisional tax payments were not enough, and you want to reduce possible interest on underpayment.

For individuals and February year-end companies, the top-up payment is usually due at the end of September.

For companies with a different year-end, it is generally due within six months after the end of the year of assessment.

## Why underestimating can cost you

The biggest risk with provisional tax is underestimating your taxable income.

If your second estimate is too low, SARS can charge an underestimation penalty.

For taxpayers with taxable income above R1 million, the second estimate must generally be at least 80% of the actual taxable income.

For taxpayers with taxable income of R1 million or less, different rules apply. SARS generally looks at whether the estimate is at least 90% of actual taxable income or at least equal to the basic amount.

This is why provisional tax should not be guessed.

A rough estimate may look fine at the time, but it can become expensive once the final income tax return is assessed.

## Late payment penalties

If provisional tax is paid late for the first or second period, SARS can charge a 10% late payment penalty.

Interest may also be charged where provisional tax is underpaid.

This is why it is important to diarise the deadlines and prepare the calculation before the due date, not on the day SARS payment is due.

## The most common mistake small businesses make

The most common mistake is treating all the money in the bank as available cash.

A business owner may look at the bank balance and think the business is doing well, but some of that money may need to be set aside for VAT, PAYE, income tax and provisional tax.

By the time the IRP6 deadline arrives, the cash has often already been spent.

Good bookkeeping helps prevent this.

When your accounts are up to date every month, you can estimate your taxable income properly and plan for provisional tax before it becomes a problem.

## How to stay on top of provisional tax

The best approach is to keep your bookkeeping up to date throughout the year.

This allows you to:

- Track your actual profit.
- Estimate taxable income more accurately.
- Set money aside for SARS.
- Avoid missed IRP6 deadlines.
- Reduce the risk of penalties and interest.
- Plan your cash flow properly.

Provisional tax should not be something you only think about twice a year. It should form part of your normal business planning.

## We help with provisional tax

At Bookkeeping Services South Africa, we help businesses and individuals calculate and submit provisional tax returns correctly.

We assist with IRP6 submissions, income tax planning, bookkeeping and SARS compliance.

Our goal is to help you avoid missed deadlines, poor estimates and unnecessary SARS penalties.

If you are not sure whether you should be paying provisional tax, or if you need help calculating your next IRP6, get in touch for a free review.

We will look at your current position, explain what needs to be done, and help you stay compliant with SARS.
`,
  },

  {
    slug: "cipc-annual-return",
    title: "CIPC Annual Returns: What Every SA Company Must File",
    description:
      "If you have a registered company or close corporation in South Africa, you must file a CIPC annual return every year. If you ignore it, your company can end up in deregistration.",
    keyword: "cipc annual return",
    category: "CIPC",
    readTime: "5 min read",
    publishedAt: "2026-04-14",
    updatedAt: "2026-04-14",
    author: "Bookkeeping Services South Africa",
    body: `## CIPC annual returns are not optional

If you have a registered company in South Africa, your CIPC annual return must be filed every year.

This applies to private companies, incorporated companies, non-profit companies, external companies and close corporations.

A CIPC annual return is not the same as your SARS income tax return.

Your SARS tax return deals with tax.

Your CIPC annual return confirms that your company or close corporation is still active and that CIPC has the correct information on record.

Even if your company is dormant or did not trade during the year, you may still need to file the annual return to keep the entity active.

## What is the purpose of a CIPC annual return?

The annual return is basically CIPC's way of checking whether the company or close corporation is still in business, or still intends to do business in the future.

It is like an annual renewal of the entity.

When you file the return, CIPC uses the information to update its records and confirm that the business should remain on the register.

If annual returns are not filed, CIPC may assume that the business is no longer active and start the deregistration process.

## When is your CIPC annual return due?

The deadline depends on the type of entity.

For **companies**, the annual return must be filed within 30 business days after the anniversary date of incorporation.

For **close corporations**, the annual return must be filed from the first day of the anniversary month up until the end of the following month.

For example, if your company was registered on 15 May, your annual return becomes due around that anniversary date every year, and you must file it within the allowed CIPC filing period.

This date is based on the company's registration date, not the SARS tax year and not the company's financial year-end.

## What does it cost?

CIPC annual return fees are based mainly on the entity type and annual turnover.

For many small private companies, the filing fee is usually low if turnover is below R1 million. As turnover increases, the CIPC fee increases.

Common company fee brackets include:

- Less than R1 million turnover.
- R1 million to R10 million turnover.
- R10 million to R25 million turnover.
- More than R25 million turnover.

Close corporations have their own fee structure.

Late filing can also lead to penalties, which means the longer you leave it, the more expensive it can become.

Because CIPC fees can change and the system calculates the fee based on the information submitted, it is best to check the amount before filing.

## What information must be filed?

When filing a CIPC annual return, you may need to confirm or submit information such as:

- Company name and registration number.
- Registered address.
- Director or member information.
- Turnover for the relevant financial period.
- Whether the business is still active.
- Beneficial ownership information.
- Financial accountability information, where required.

CIPC also requires companies and close corporations to deal with their Beneficial Ownership declaration, and either Annual Financial Statements or a Financial Accountability Supplement, depending on what applies to the entity.

Some companies may also need to complete a compliance checklist.

This is why CIPC annual returns should not be treated as just a quick payment. The information must be checked properly.

## CIPC annual return vs SARS tax return

This is where many business owners get confused.

A SARS tax return is filed with SARS and deals with income tax.

A CIPC annual return is filed with CIPC and deals with keeping the company or close corporation active on the CIPC register.

Filing one does not replace the other.

You can be up to date with SARS but still non-compliant with CIPC.

You can also be up to date with CIPC but still have outstanding SARS returns.

Both must be managed properly.

## What happens if you do not file?

If annual returns are not filed, CIPC can begin the deregistration process.

This can lead to the company or close corporation being marked as being in deregistration, and eventually being finally deregistered.

When a company is finally deregistered, it loses its legal existence as a registered juristic person.

This can create serious problems.

- The business may struggle to trade.
- Banks may restrict or review the business account.
- Contracts, tenders and supplier registrations may be affected.
- Assets registered in the company's name can become difficult to deal with.
- The company name may eventually become available for someone else to reserve.

Reinstatement may be possible in some cases, but it is not something any business owner wants to deal with. It can take time, cost money, and usually requires the outstanding annual returns and supporting documents to be brought up to date.

## When does deregistration start?

CIPC can start deregistration when annual returns remain outstanding.

In practice, if two or more successive annual returns are outstanding, the company or close corporation is at risk of being placed into the annual return deregistration process.

Once the status changes, the business owner must act quickly.

Leaving it until final deregistration makes the problem more difficult and more expensive to fix.

## How to file a CIPC annual return

CIPC annual returns are filed electronically.

The basic process is:

1. Log in to the CIPC annual returns or eServices platform.
2. Select the company or close corporation.
3. Confirm the required company information.
4. Enter the turnover information.
5. Submit any required Beneficial Ownership, AFS or FAS information.
6. Pay the calculated CIPC fee.
7. Save proof of filing and payment.

After filing, keep the confirmation for your records.

If you are applying for funding, tenders, supplier registration or business finance, you may be asked to show that your company is active and compliant.

## Why business owners forget CIPC returns

Most business owners focus on SARS, VAT, PAYE, invoices and clients.

CIPC is easy to forget because the annual return only comes around once a year.

The problem is that missing it can cause serious admin issues later.

A company may look fine from the outside, but if CIPC annual returns are outstanding, the business may already be at risk.

That is why it is better to track the deadline every year and file before penalties or deregistration become a problem.

## We help keep your CIPC compliance up to date

At Bookkeeping Services South Africa, we help business owners stay on top of CIPC annual returns and general compliance.

We assist with checking your company status, identifying outstanding annual returns, filing CIPC returns, and helping bring old compliance issues up to date.

If your company is behind on CIPC annual returns, or you are not sure whether your company is still active, get in touch for a free compliance review.

We will check what is outstanding, explain what needs to be filed, and help you keep your company in good standing.
`,
  },

  {
    slug: "small-business-bookkeeping-south-africa",
    title: "Small Business Bookkeeping in South Africa: DIY Guide for Under-10-Staff SMEs",
    description:
      "How to do your own bookkeeping as a small SA business — what SARS requires, which software to use, and the exact point at which DIY stops paying off.",
    keyword: "small business bookkeeping",
    category: "Bookkeeping",
    readTime: "8 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-26",
    author: "Bookkeeping Services South Africa",
    body: `## What small business bookkeeping really means

Bookkeeping is the day-to-day recording of every rand that moves in and out of your business. Done well, it gives you real-time visibility on profitability, keeps you compliant with SARS, and produces the records you need for tax returns and funding applications.

For a South African small business, bookkeeping usually covers:

- Recording sales and customer invoices
- Recording supplier invoices and expenses
- Reconciling business bank accounts every month
- Tracking VAT (if registered)
- Running payroll (if you have staff)
- Preparing monthly management figures
- Producing year-end annual financial statements

## Records SARS requires you to keep

SARS requires you to keep business records for **at least 5 years** from the date you submitted the return. That includes:

- Bank statements
- Sales invoices and till slips
- Supplier invoices and receipts
- Payroll records (EMP201, EMP501, IRP5s, UIF)
- VAT records (if registered)
- Asset registers

Records can be electronic, but they must be readable and accessible.

## Software options

The three most common cloud accounting platforms in South Africa are:

- **Xero** — clean interface, strong app integrations, great for service businesses.
- **Sage Business Cloud Accounting** — local SA provider, well integrated with SA payroll.
- **QuickBooks** — strong for sole proprietors and freelancers.

Spreadsheets work for the very smallest businesses but fall apart quickly once you have VAT or staff.

## DIY vs outsourced

Doing your own books is realistic when:
- You have <10 transactions a month
- You're not VAT registered
- You have no employees

Outsourcing typically pays for itself once you:
- Register for VAT
- Hire your first employee
- Spend more than a couple of hours a week on books
- Miss a SARS or CIPC deadline

## What to ask before hiring a bookkeeper

1. Are you SAICA, SAIPA or SAIBA/CIBA registered?
2. Is your fee fixed monthly?
3. Which SARS and CIPC submissions are included?
4. How quickly will I get a response?
5. Will I get monthly management accounts?

## Get a free quote

We provide monthly bookkeeping packages from R300/month, with all SARS and CIPC submissions included. [Contact us](/contact) for a free quote.
`,
  },
  {
    slug: "outsourced-bookkeeping-south-africa",
    title: "Outsourced vs In-House Bookkeeping in SA: The Real Cost Comparison",
    description:
      "Line-by-line cost of an in-house SA bookkeeper (salary, UIF, SDL, leave, software, cover) vs a monthly outsourced retainer — with the numbers.",
    keyword: "outsourced bookkeeping",
    category: "Bookkeeping",
    readTime: "6 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-26",
    author: "Bookkeeping Services South Africa",
    body: `## What "outsourced bookkeeping" means

Outsourced bookkeeping is when an external firm handles your books, payroll, VAT and SARS/CIPC submissions on a monthly retainer — instead of you hiring an in-house bookkeeper.

For most South African SMEs it works out cheaper, more reliable, and less risky than doing it in-house.

## In-house vs outsourced — what it really costs

| Cost item | In-house bookkeeper | Outsourced |
| --- | --- | --- |
| Salary | R18,000 – R30,000/month | Included |
| UIF / SDL / leave | Yes | No |
| Software licences | You pay | Often included |
| Training / CPD | You pay | Provider's problem |
| Cover during leave / sick | You pay | Provider's problem |
| Risk of errors | You carry it | Shared with firm |
| Typical total monthly | R22,000+ | R2,500 – R6,000 |

## When outsourcing makes sense

- You have under 50 staff
- You don't need a finance person on-site
- You'd rather pay a fixed monthly fee
- You want SAICA-level oversight without a SAICA-level salary
- You've outgrown DIY but don't need a full-time hire

## When it doesn't

- You operate at scale with daily finance ops (then hire in-house but still keep an external accountant for tax/AFS).
- You need someone physically managing cash, stock and walk-in receipts.

## What to look for in a provider

- SAICA, SAIPA or SAIBA/CIBA registered
- Fixed monthly fee
- All SARS submissions included (VAT, PAYE, ITR14)
- CIPC annual return included
- Responsive — replies within one working day
- South African team familiar with SARS eFiling and EasyFile

## Our offer

We provide outsourced bookkeeping packages from **R300 – R4,500/month** depending on turnover and complexity. All packages include the relevant SARS and CIPC submissions.

[Talk to a qualified accountant →](/book-a-call)
`,
  },
  {
    slug: "bookkeepers-near-me-south-africa",
    title: "'Bookkeepers Near Me' in South Africa: How to Vet Local and Remote Options",
    description:
      "A vetting checklist for hiring a SA bookkeeper — SAICA / SAIPA registration, tax practitioner status, red flags, and why remote usually wins.",
    keyword: "bookkeepers near me",
    category: "Bookkeeping",
    readTime: "5 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-26",
    author: "Bookkeeping Services South Africa",
    body: `## Local vs remote — does it actually matter?

Most South African bookkeeping today is done remotely on Xero, Sage or QuickBooks. You upload your bank feed and documents, and your bookkeeper does the rest.

That means the bookkeeper down the road has no real advantage over a qualified one across the country — as long as you can reach them quickly and they understand SARS and CIPC requirements.

## What actually matters

1. **Registration** — look for SAICA, SAIPA or SAIBA/CIBA membership.
2. **Tax practitioner status** — required to submit on your behalf to SARS.
3. **Scope** — bookkeeping, VAT, payroll, CIPC, tax, AFS.
4. **Fixed monthly fee** — avoid surprise hourly billing.
5. **Response time** — get this in writing.
6. **References** — ask for two existing clients you can speak to.

## Red flags

- No professional registration
- Won't put fees in writing
- Cash-only or no proper invoicing
- Can't access SARS eFiling or EasyFile
- Slow to respond before you've even signed up

## We serve businesses nationwide

We provide remote bookkeeping across every province in South Africa. See the local hub for your area:

- [Johannesburg](/areas/johannesburg)
- [Cape Town](/areas/cape-town)
- [Durban](/areas/durban)
- [Pretoria](/areas/pretoria)
- [Sandton](/areas/sandton)
- [Centurion](/areas/centurion)
- [Umhlanga](/areas/umhlanga)
- [Gqeberha (Port Elizabeth)](/areas/gqeberha)
- [East London](/areas/east-london)
- [Bloemfontein](/areas/bloemfontein)
- [Polokwane](/areas/polokwane)
- [Mbombela (Nelspruit)](/areas/mbombela)
- [Stellenbosch](/areas/stellenbosch)
- [Randburg](/areas/randburg)
- [Midrand](/areas/midrand)
- [All areas we serve](/areas)

Or read our [complete guide to bookkeeping in South Africa](/bookkeeping) if you want to understand what a SAICA registered bookkeeper actually does before you hire one.

[Book a free 15-minute call →](/book-a-call)
`,
  },
  {
    slug: "how-to-register-for-vat-south-africa",
    title: "How to Register for VAT in South Africa (VAT101 Step-by-Step)",
    description: "A practical 2026 guide to VAT registration with SARS — when it's compulsory, what documents you need, and how the biometric verification works.",
    keyword: "vat registration south africa",
    category: "SARS",
    readTime: "6 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-27",
    author: "Bookkeeping Services South Africa",
    body: `## Who must register for VAT?

You **must** register for VAT in South Africa when your taxable turnover exceeds **R2.3 million in any continuous 12-month period** (threshold effective from 1 April 2026) — or when you reasonably expect to exceed R2.3m within the next 12 months under a written contract.

You **may** register voluntarily from **R120,000** in 12-month turnover (also effective 1 April 2026), provided you meet SARS' requirements.

## What you need before applying

- Company registration documents (CoR 14.3 or CoR 15.1A)
- SARS income tax reference number (active and tax-compliant)
- Proof of business address (utility bill, lease)
- Bank confirmation letter (less than 3 months old)
- ID and proof of address for the public officer
- 3 most recent invoices or contracts showing turnover

## Step 1 — log into eFiling

Add the VAT product to your eFiling profile under **Organisations → SARS Registered Details → VAT**.

## Step 2 — complete VAT101

The VAT101 form opens in eFiling. Capture:

- Expected turnover (next 12 months)
- VAT category (A or B — SARS assigns)
- Bank details for refunds
- Filing frequency

## Step 3 — biometric verification

Since 2023, SARS requires every new VAT vendor to attend a biometric verification appointment at a SARS branch. Book through eFiling. Bring originals of every document.

## Step 4 — wait for the VAT number

Allow up to **21 business days** post-verification. Once approved, you'll receive a VAT103 notice with your VAT number.

## Common reasons SARS rejects a VAT registration

- Income tax not up to date
- Public officer not appointed
- Bank account in a personal name
- Insufficient proof of trading

## We handle the entire process

For clients on our Medium package (R4,500/month), VAT registration and bi-monthly VAT201 submissions are included.

[Talk to a Qualified Accountant →](/book-a-call) · [VAT Calculator →](/tools/vat-calculator)
`,
  },
  {
    slug: "vat201-filing-guide",
    title: "VAT201 Filing Guide: How to Submit Your VAT Return to SARS",
    description: "Step-by-step guide to filing your bi-monthly VAT201 return on SARS eFiling, including common mistakes that trigger audits.",
    keyword: "vat201 filing",
    category: "SARS",
    readTime: "7 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-26",
    author: "Bookkeeping Services South Africa",
    body: `## What is the VAT201?

The VAT201 is the **bi-monthly VAT return** every South African VAT vendor must submit to SARS. It declares output VAT (charged to customers) and input VAT (claimed on expenses), and calculates what you owe — or what SARS owes you.

## When is VAT201 due?

- **eFiling submissions:** last business day of the month following the VAT period
- **Manual submissions:** 25th of the month following

**Category A** vendors file: Jan/Mar/May/Jul/Sep/Nov
**Category B** vendors file: Feb/Apr/Jun/Aug/Oct/Dec

## How to file on eFiling

1. Log in → **Returns → VAT201**
2. Select the period
3. Capture **standard-rated supplies** (15%) under Field 1
4. **Zero-rated supplies** under Field 2 (exports, basic foods)
5. **Exempt supplies** under Field 3
6. **Capital input VAT** (vehicles, equipment) under Field 14
7. **Other input VAT** under Field 15
8. Review the calculated amount
9. Submit and pay via eFiling Credit Push

## What SARS verifies

Approximately 1 in 5 returns are flagged for verification. SARS will request:

- Bank statements for the period
- Detailed VAT input listing
- Top 10 supplier invoices
- Top 10 customer invoices

Refunds are paid within 21 business days **after** verification clears.

## Common VAT201 mistakes

- Claiming VAT on **motor cars** (section 17(2) disallows)
- Claiming VAT on **entertainment** (also disallowed)
- Filing **nil returns** when there was activity
- Output VAT on **zero-rated** or **exempt** supplies
- Wrong VAT period selected

## Late filing penalties

- 10% penalty on VAT owing
- Interest at the prescribed rate (currently 11.75%)
- Possible Administrative Penalty for repeat offenders

[Need help? Talk to a Qualified Accountant →](/book-a-call)
`,
  },
  {
    slug: "paye-explained-south-africa",
    title: "PAYE Explained: How South African Income Tax is Deducted from Your Salary",
    description: "Plain-English guide to PAYE in South Africa — how employers calculate it, what rebates apply, and how to check your IRP5 is correct.",
    keyword: "paye south africa",
    category: "SARS",
    readTime: "6 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-27",
    author: "Bookkeeping Services South Africa",
    body: `## What is PAYE?

PAYE — **Pay-As-You-Earn** — is South African income tax deducted from your salary each month and paid over to SARS by your employer via the EMP201 return.

## How PAYE is calculated

1. Annualise your monthly remuneration (× 12)
2. Apply the SARS tax tables (2026/27)
3. Deduct the **primary rebate** (R17,235), plus secondary (65+) and tertiary (75+) rebates
4. Divide back by 12



## 2026/2027 SARS tax brackets

| Taxable income | Rate of tax |
|---|---|
| R0 – R237,100 | 18% |
| R237,101 – R370,500 | 26% |
| R370,501 – R512,800 | 31% |
| R512,801 – R673,000 | 36% |
| R673,001 – R857,900 | 39% |
| R857,901 – R1,817,000 | 41% |
| R1,817,001 + | 45% |

## Tax credits that reduce your PAYE

- **Medical aid tax credit**: R364 main + R364 first dependant + R246 each additional (2026/27)
- **Retirement annuity / pension fund**: deduct up to 27.5% of remuneration, capped R350,000/year
- **Travel allowance**: 80% taxed monthly; refund via ITR12 logbook

## UIF and SDL

In addition to PAYE:
- **UIF**: 1% from employee + 1% from employer, capped at R17,712/month per employee
- **SDL**: 1% from employer, only if total annual payroll exceeds R500,000

**Important:** SARS requires that an employer registered (or required to register) for PAYE or SDL must **also register for UIF contributions** with SARS. UIF is not optional once PAYE applies.

## Your IRP5 — what to check

Every year by **end May**, your employer issues an IRP5 showing total income, PAYE, UIF and SDL. Check that:

- Your income matches your payslips
- PAYE matches your monthly deductions
- IRP5 source codes are correct
- It's been submitted to SARS (visible on eFiling)

## We do payroll for businesses

If you run payroll for staff, our packages from R2,500/month handle PAYE calculation, EMP201, EMP501 and IRP5 issuance.

[See Payroll Services →](/payroll-services)
`,
  },
  {
    slug: "emp201-vs-emp501-explained",
    title: "EMP201 vs EMP501: Every SA Employer's Payroll Submission Guide",
    description: "Understand the difference between EMP201 and EMP501 — when each is due, what they report, and the penalties for missing the deadlines.",
    keyword: "emp201 emp501",
    category: "SARS",
    readTime: "5 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-27",
    author: "Bookkeeping Services South Africa",
    body: `## The short version

- **EMP201** — monthly. Reports PAYE, UIF, SDL deducted that month.
- **EMP501** — bi-annual. Reconciles all EMP201s and IRP5s for the period.

Get either wrong and SARS imposes 10% penalties + interest.

## EMP201 in detail

The EMP201 is filed by the **7th of every month** (or last business day before, if the 7th falls on a weekend or public holiday).

It declares:
- PAYE deducted from employees
- UIF (employer + employee 1% each — compulsory once registered for PAYE/SDL)
- SDL (1% employer, if payroll > R500k/year)
- ETI (Employment Tax Incentive) claims

Pay via eFiling Credit Push.

## EMP501 in detail

The EMP501 is filed **twice a year**:

- **Interim** — September/October (for March–August period)
- **Annual** — April/May (for full March–February period)

It reconciles:
- All EMP201 submissions for the period
- IRP5 and IT3(a) certificates issued
- Total PAYE, UIF, SDL and ETI

Mismatches between EMP201 totals and EMP501 trigger a reconciliation difference — SARS will recover the difference plus penalties.

## Common pitfalls

- Late filing — 10% penalty + interest
- IRP5 source codes incorrect (e.g. 3601 vs 3605)
- ETI claims without satisfying the criteria
- Forgetting the interim EMP501

## We handle payroll from R2,500/month

Our packages include EMP201 monthly, EMP501 bi-annual, UIF declarations and IRP5 issuance — all on time, every time.

[Get a free payroll quote →](/book-a-call)
`,
  },
  {
    slug: "cipc-beneficial-ownership-guide",
    title: "CIPC Beneficial Ownership: What South African Companies Must Disclose",
    description: "Since 2023, every SA company must declare its beneficial owners to CIPC. Here's who qualifies, what to file, and the R10m penalty for non-compliance.",
    keyword: "cipc beneficial ownership",
    category: "CIPC",
    readTime: "6 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-26",
    author: "Bookkeeping Services South Africa",
    body: `## Why this rule exists

South Africa was grey-listed by the **FATF** (Financial Action Task Force) in February 2023 over weak controls on money laundering and terror financing. In response, CIPC introduced mandatory **Beneficial Ownership (BO)** disclosure for every registered company.

## Who is a Beneficial Owner?

A **natural person** who, directly or indirectly:

- Owns **5% or more** of the company's shares, OR
- Exercises effective control over the company, OR
- Holds the right to appoint or remove the majority of directors

Even if shares are held through trusts, holding companies or nominees — the ultimate human owner must be disclosed.

## What you must file

For each BO:

- Full names and ID/passport
- Residential address
- Nationality and date of birth
- % ownership or nature of control
- Date control was acquired

Filing is via the CIPC BO portal at **bo.cipc.co.za**, integrated with your CIPC profile.

## When to file

- With every **annual return**
- Within **10 business days** of any change in beneficial ownership

## Penalties for non-compliance

- Fine up to **R10 million** OR 10% of turnover
- Imprisonment up to **10 years**
- Directors personally liable
- Company placed on non-compliance register — affects banking and tendering

## Common compliance traps

- Trusts as shareholders — you must look through to the trust beneficiaries
- Holding company structures — disclose the ultimate natural person
- Nominee arrangements — disclose the true beneficial owner
- Share transfers not reported within 10 days

## We file BO with every annual return

Included in our Basic, Entry and Medium packages.

[Talk to a Qualified Accountant →](/book-a-call)
`,
  },
  {
    slug: "restore-deregistered-company-cipc",
    title: "How to Restore a Deregistered Company in South Africa (CoR 40.5)",
    description: "Step-by-step process to restore a deregistered company at CIPC — including the SARS no-objection letter, newspaper advert, and outstanding returns.",
    keyword: "restore deregistered company",
    category: "CIPC",
    readTime: "7 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-26",
    author: "Bookkeeping Services South Africa",
    body: `## What deregistration means

A deregistered company has been **struck off the CIPC register**, usually because annual returns weren't filed for two consecutive years. Consequences:

- The company **no longer legally exists**
- Bank accounts can be frozen
- Contracts may be unenforceable
- Directors are personally exposed
- Assets vest in the State (bona vacantia)

## Step 1 — apply for restoration (CoR 40.5)

File the CoR 40.5 with CIPC, attaching:

- Reason for restoration (e.g. "still actively trading")
- Disclosure of any assets that vested in the State
- Authorising resolution from directors

## Step 2 — SARS Letter of No Objection

Obtain a letter from SARS confirming no objection to restoration. Requires:

- Company's tax affairs up to date
- All outstanding returns filed
- Any tax debts settled or arranged

This is often the longest step — allow **2–4 weeks**.

## Step 3 — newspaper advertisement

Publish a notice of intended restoration in a newspaper circulating in the company's registered area. Keep the original page and the publisher's invoice.

## Step 4 — sworn affidavit

A director must sign an affidavit confirming:

- The company was active at deregistration
- No assets vested in the State (or details if they did)
- The reason for missed filings

## Step 5 — pay outstanding annual returns

CIPC will demand:

- All missing annual returns
- Penalties for each
- The CoR 40.5 application fee

## Timeline

- 6–10 weeks from application to reinstatement
- Faster if SARS clearance is already current

## We handle full restoration

We've restored hundreds of SA companies. Flat fee from **R5,500** depending on outstanding returns.

[Get a restoration quote →](/contact)
`,
  },
  {
    slug: "bank-reconciliation-guide-south-africa",
    title: "Bank Reconciliation: The Most Important Bookkeeping Task You're Probably Skipping",
    description: "Why bank reconciliation matters, how to do it correctly in Xero or Sage, and what to look for to catch fraud, errors and missing transactions.",
    keyword: "bank reconciliation",
    category: "Bookkeeping",
    readTime: "6 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-26",
    author: "Bookkeeping Services South Africa",
    body: `## What is bank reconciliation?

Bank reconciliation is the process of **matching every transaction on your bank statement to a corresponding entry in your accounting records**. The two must agree, to the cent, at the end of every period.

## Why it matters

Without reconciliation:

- Duplicate or missed transactions go undetected
- Fraudulent payments slip through
- VAT and tax returns are submitted on wrong numbers
- Bank balance in your books drifts from reality
- Auditors and SARS reject the financials

## How to reconcile in Xero (the easy way)

1. Connect a **bank feed** from your SA bank (FNB, Standard Bank, ABSA, Nedbank, Capitec, Investec — all supported)
2. Transactions appear in Xero daily
3. Click **Reconcile**, match each line to an invoice/bill, or create a Spend Money / Receive Money
4. Aim for **zero unreconciled items** by month-end

## What to look for

- **Unexpected withdrawals** — could be fraud or unauthorised debits
- **Duplicate payments** — common with manual EFTs
- **Bank charges** — must be captured as expenses
- **Returned debits / NSF fees** — affects cash flow forecasts
- **Interest received** — taxable income, must be declared

## Common reconciliation mistakes

- Force-matching items without checking the amount
- Skipping transactions you don't recognise
- Ignoring small differences (R0.50 today → R5,000 in a year)
- Not reconciling at all between SARS submissions

## We reconcile monthly for every client

Bank reconciliation is built into every package — we don't submit your VAT, payroll or tax without first reconciling your bank.

[Browse Packages →](/packages)
`,
  },
  {
    slug: "xero-vs-sage-vs-quickbooks-south-africa",
    title: "Xero vs Sage vs QuickBooks: Which Accounting Software is Best for SA?",
    description: "Honest comparison of the three most popular accounting platforms in South Africa — features, pricing, bank feeds, payroll and which suits your business.",
    keyword: "xero vs sage south africa",
    category: "Bookkeeping",
    readTime: "8 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-26",
    author: "Bookkeeping Services South Africa",
    body: `## The three SA market leaders

Most SA bookkeeping is done on one of three platforms:

- **Xero** — modern, cloud-first, growing fastest
- **Sage** (Business Cloud Accounting) — long-standing SA market leader
- **QuickBooks Online** — global brand, growing locally

We use **Xero** by default. Here's why — and where the alternatives win.

## Xero

**Strengths:**
- Cleanest interface — easy to learn
- Best bank feeds for SA banks (FNB, Standard Bank, ABSA, Nedbank, Capitec, Investec)
- Strong app ecosystem (Receipt Bank, A2X, Hubdoc)
- Unlimited users on every plan
- Multi-currency on higher plans

**Weaknesses:**
- Payroll is basic (most SA firms use SimplePay or Sage Payroll alongside)
- More expensive than Sage at entry tier

**Pricing (2026):** Starter R310/m · Standard R600/m · Premium R815/m

## Sage Business Cloud Accounting

**Strengths:**
- Built for SA — VAT201, EMP201 and CIPC integration native
- Cheapest at entry level
- Sage Payroll integrates seamlessly
- Strong SARS eFiling integration

**Weaknesses:**
- Interface feels dated
- Bank feeds less reliable than Xero
- Smaller app ecosystem

**Pricing (2026):** Start R165/m · Accounting R270/m

## QuickBooks Online

**Strengths:**
- Globally recognised — easier for foreign owners
- Strong reporting
- Good mobile app

**Weaknesses:**
- Bank feeds for SA banks less reliable
- Smaller local accountant network
- VAT compliance requires more manual work

**Pricing (2026):** Simple Start R190/m · Essentials R340/m · Plus R450/m

## Our recommendation

- **Solo / first-time entrepreneur:** Sage Start (cheap, SA-native)
- **Growing SME (R500k–R12m turnover):** Xero (we'll set it up)
- **International business or franchise:** QuickBooks Online

## We migrate you free

Switching is included in our onboarding — no extra fee.

[Get a free setup →](/book-a-call)
`,
  },
  {
    slug: "bookkeeping-vs-accounting-difference",
    title: "Bookkeeping vs Accounting: What's the Difference (and Which Do You Need)?",
    description: "Bookkeeping captures the data. Accounting interprets it. Tax compliance turns it into SARS submissions. Here's how the three layers work — and what they cost.",
    keyword: "bookkeeping vs accounting",
    category: "Bookkeeping",
    readTime: "5 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-26",
    author: "Bookkeeping Services South Africa",
    body: `## The three layers, in plain English

1. **Bookkeeping** — recording every transaction in the books
2. **Accounting** — interpreting the books and producing financial statements
3. **Tax compliance** — turning the financials into SARS submissions

A business needs all three. They can be done by one provider (us) or three separate ones (expensive and slow).

## Bookkeeping — the foundation

Daily/weekly work:

- Capture sales and expenses
- Reconcile bank accounts
- Process supplier invoices
- Run payroll
- File VAT201, EMP201, EMP501

Typically done by a **bookkeeper** (Certified or Registered with SAIBA).

## Accounting — the interpretation

Monthly/annual work:

- Management accounts (P&L, balance sheet)
- Cash flow forecasts
- Budget vs actual analysis
- Annual Financial Statements (AFS)
- Business advice

Typically done by an **accountant** — ideally SAICA registered.

## Tax compliance — the regulatory layer

- ITR14 (company income tax)
- ITR12 (sole proprietor / director)
- IRP6 (provisional tax × 2)
- VAT201 reconciliation
- CIPC annual returns
- Tax clearance certificates

Done by a **tax practitioner** registered with a Recognised Controlling Body (RCB).

## What it costs to use three separate providers

| Service | Going rate |
|---|---|
| Bookkeeper | R1,500–R3,000/m |
| Accountant | R8,000–R15,000/year |
| Tax practitioner | R3,000–R8,000/year |
| **Total** | **R28,500–R59,000/year** |

## Our integrated packages

| Package | Includes | Monthly |
|---|---|---|
| Basic | Tax + CIPC for dormant company | R300 |
| Entry | Bookkeeping + payroll + tax + CIPC up to R2.3m | R2,500 |
| Medium | Above + VAT201 + workers' comp up to R12m | R4,500 |

[See Packages →](/packages)
`,
  },
  {
    slug: "sars-auto-assessment-guide",
    title: "SARS Auto-Assessment: What to Do When You Get One",
    description: "SARS issues millions of auto-assessments each tax season. Should you accept it, edit it, or reject it? Here's how to decide — and avoid paying tax you don't owe.",
    keyword: "sars auto-assessment",
    category: "SARS",
    readTime: "5 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-26",
    author: "Bookkeeping Services South Africa",
    body: `## What is an auto-assessment?

SARS pre-populates an income tax return (ITR12) using data from:

- IRP5 (employer)
- IT3(a) — investment income
- IT3(b) — interest income
- IT3(c) — capital gains
- Medical aid certificates
- Retirement annuity certificates

If you don't object within the deadline (usually 40 business days), SARS treats it as **filed and assessed**.

## Should you accept it?

Accept if:

- You only earned salary income (one IRP5)
- No business income
- No travel allowance
- No commission
- No additional medical expenses
- No rental income

**Edit or reject if:**

- You had additional deductions (home office, business expenses)
- You earned freelance/commission income
- You have a travel allowance with logbook
- You contributed to a retirement annuity outside payroll
- You incurred out-of-pocket medical expenses
- You have rental property

## How to edit an auto-assessment

1. Log in to eFiling
2. Open the ITR12
3. Click **Edit Return**
4. Add deductions, additional income, supporting docs
5. Submit before the deadline

## The risk of just accepting

Every year, thousands of South Africans accept an auto-assessment that **owes them money** — refunds they never claim. Others accept assessments that miss income, leading to penalties when SARS audits later.

## We review every auto-assessment for clients

Included in every package. We won't let you overpay SARS.

[Talk to a Qualified Accountant →](/book-a-call)
`,
  },
  {
    slug: "cash-flow-management-sa-small-business",
    title: "Cash Flow Management for SA Small Businesses: A Practical Playbook",
    description: "Cash flow kills more SA small businesses than lack of profit. Here's how to forecast, control and protect cash flow using simple bookkeeping habits.",
    keyword: "cash flow management south africa",
    category: "Bookkeeping",
    readTime: "7 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-27",
    author: "Bookkeeping Services South Africa",
    body: `## Profit is not cash

You can be profitable on paper and bankrupt in the bank. That's because profit recognises invoiced sales — cash flow only recognises **money actually in the account**.

## The 4 cash flow killers in SA SMEs

1. **30/60/90 day customer terms** — common in corporate and government
2. **Front-loaded VAT** — you pay VAT to SARS before customers pay you
3. **Payroll on the 25th** — when most invoices are due on the 30th
4. **Tax surprises** — provisional tax shocks twice a year

## Step 1 — build a 13-week rolling forecast

Forecast cash IN and cash OUT for the next 13 weeks. Update weekly. Xero, Sage and Float all do this automatically.

## Step 2 — shorten your debtor days

- Invoice immediately, not month-end
- Offer 2% settlement discount for 7-day payment
- Use Xero/Sage automated reminders
- Charge interest on overdue accounts (mandi tory mention on invoice terms)

## Step 3 — stretch your creditor days

- Negotiate 30 → 45-day terms with suppliers
- Pay by credit card (free 30-day float)
- Use trade finance facilities

## Step 4 — separate tax money

Move roughly 27% of every receipt into a **separate savings account**:

- 15% for VAT
- 7% for company tax (normal company tax rate is 27% for 2026/2027 — adjust if you qualify as a Small Business Corporation)
- 5% buffer for payroll surprises

## Step 5 — monitor a cash flow dashboard monthly

Track:

- Cash on hand vs 90-day fixed costs
- Debtor days (target < 45)
- Creditor days (target > 30)
- Quick ratio (target > 1)

## We build cash flow dashboards for clients

Included on our Medium package. We meet quarterly to review.

[Browse Packages →](/packages)
`,
  },
  {
    slug: "annual-financial-statements-south-africa",
    title: "Annual Financial Statements (AFS) in South Africa: What You Need to Know",
    description: "Who must prepare AFS, what the Companies Act requires, audit vs review vs compilation, and how SARS uses them for your ITR14.",
    keyword: "annual financial statements south africa",
    category: "Bookkeeping",
    readTime: "6 min read",
    publishedAt: "2026-06-26",
    updatedAt: "2026-06-26",
    author: "Bookkeeping Services South Africa",
    body: `## What are AFS?

Annual Financial Statements are the **end-of-year financial report** of a company. They include:

- Statement of Comprehensive Income (P&L)
- Statement of Financial Position (Balance Sheet)
- Statement of Cash Flows
- Notes to the financials
- Directors' Report

## Who must prepare AFS?

**Every company** registered in South Africa, regardless of size or trading status. The Companies Act requires AFS within **6 months of year-end**.

## Audit, Review or Compilation?

Determined by your **Public Interest Score (PIS)** — a Companies Act formula based on employees, third-party debt, turnover and shareholders.

| PIS | Requirement |
|---|---|
| < 100 | Compilation (we prepare; no external review) |
| 100 – 349 | Independent Review (by Chartered Accountant) |
| ≥ 350 | Audit (registered auditor) |

Plus: companies that hold assets in a fiduciary capacity, or where the MOI requires it.

## How AFS link to SARS

Your AFS feed directly into your **ITR14** (company income tax return). Mismatches between AFS and ITR14 trigger SARS verifications.

## Common AFS mistakes

- Missing notes to financials
- Directors' Report incomplete
- Bank reconciliation off
- Provisions for tax incorrect
- Comparative period missing

## Filing AFS

- **CIPC:** Companies with PIS ≥ 350 must file AFS via XBRL
- **SARS:** Submit with ITR14
- **Shareholders:** Provide a copy

## We prepare AFS for every client

Included in Entry and Medium packages. SAICA registered — accepted by all banks and lenders.

[Browse Packages →](/packages) · [AFS Service →](/annual-financial-statements)
`,
  },
];

export const getPostBySlug = (slug: string) => posts.find((p) => p.slug === slug);
